import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/database';
import type { PaletteAccessRecord } from '../lib/database';
import {
  canPerformAction,
  getUserRole,
  sharePalette,
  revokePaletteAccess,
  setPublicAccess,
  transferOwnership,
  type PaletteRole,
  type Permission,
} from '../lib/permissions';

export interface UsePalettePermissionsResult {
  access: PaletteAccessRecord | null;
  userRole: PaletteRole | null;
  loading: boolean;
  error: string | null;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
  canExport: boolean;
  canManagePermissions: boolean;
  hasPermission: (permission: Permission) => boolean;
  shareWith: (userId: string, role: PaletteRole) => Promise<void>;
  revokeAccess: (userId: string) => Promise<void>;
  makePublic: (isPublic: boolean) => Promise<void>;
  transferOwner: (newOwnerId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing palette permissions and access control
 */
export function usePalettePermissions(paletteId: string | null): UsePalettePermissionsResult {
  const [access, setAccess] = useState<PaletteAccessRecord | null>(null);
  const [userRole, setUserRole] = useState<PaletteRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Load current user ID
  useEffect(() => {
    const loadUserId = async () => {
      try {
        const settings = await db.getUserSettings();
        setUserId(settings.id!);
      } catch (err) {
        setError(`Failed to load user settings: ${err}`);
      }
    };
    loadUserId();
  }, []);

  // Load palette access control
  const refresh = useCallback(async () => {
    if (!paletteId || !userId) {
      setAccess(null);
      setUserRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessRecord = await db.getPaletteAccess(paletteId);
      
      if (!accessRecord) {
        // No access record found - create one for backward compatibility
        // Assume current user is owner of existing palettes without access records
        await db.createPaletteAccess(paletteId, userId);
        const newAccessRecord = await db.getPaletteAccess(paletteId);
        setAccess(newAccessRecord || null);
        setUserRole(newAccessRecord ? getUserRole(userId, newAccessRecord) : null);
      } else {
        setAccess(accessRecord);
        setUserRole(getUserRole(userId, accessRecord));
      }
    } catch (err) {
      setError(`Failed to load permissions: ${err}`);
      setAccess(null);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  }, [paletteId, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Permission check helper
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!access || !userId) return false;
      return canPerformAction(userId, access, permission);
    },
    [access, userId]
  );

  // Share palette with a user
  const shareWith = useCallback(
    async (targetUserId: string, role: PaletteRole) => {
      if (!paletteId || !access || !userId) {
        throw new Error('Cannot share: missing palette or access information');
      }

      if (!canPerformAction(userId, access, 'palette:share')) {
        throw new Error('You do not have permission to share this palette');
      }

      try {
        const updatedAccess = sharePalette(access, targetUserId, role, userId);
        await db.updatePaletteAccess(paletteId, {
          sharedWith: updatedAccess.sharedWith,
        });
        await refresh();
      } catch (err) {
        throw new Error(`Failed to share palette: ${err}`);
      }
    },
    [paletteId, access, userId, refresh]
  );

  // Revoke access from a user
  const revokeAccess = useCallback(
    async (targetUserId: string) => {
      if (!paletteId || !access || !userId) {
        throw new Error('Cannot revoke access: missing palette or access information');
      }

      if (!canPerformAction(userId, access, 'palette:manage_permissions')) {
        throw new Error('You do not have permission to manage access');
      }

      try {
        const updatedAccess = revokePaletteAccess(access, targetUserId);
        await db.updatePaletteAccess(paletteId, {
          sharedWith: updatedAccess.sharedWith,
        });
        await refresh();
      } catch (err) {
        throw new Error(`Failed to revoke access: ${err}`);
      }
    },
    [paletteId, access, userId, refresh]
  );

  // Make palette public or private
  const makePublic = useCallback(
    async (isPublic: boolean) => {
      if (!paletteId || !access || !userId) {
        throw new Error('Cannot change visibility: missing palette or access information');
      }

      if (!canPerformAction(userId, access, 'palette:share')) {
        throw new Error('You do not have permission to change visibility');
      }

      try {
        const updatedAccess = setPublicAccess(access, isPublic);
        await db.updatePaletteAccess(paletteId, {
          isPublic: updatedAccess.isPublic,
          publicRole: updatedAccess.publicRole,
        });
        await refresh();
      } catch (err) {
        throw new Error(`Failed to change visibility: ${err}`);
      }
    },
    [paletteId, access, userId, refresh]
  );

  // Transfer ownership
  const transferOwner = useCallback(
    async (newOwnerId: string) => {
      if (!paletteId || !access || !userId) {
        throw new Error('Cannot transfer ownership: missing palette or access information');
      }

      if (access.ownerId !== userId) {
        throw new Error('Only the owner can transfer ownership');
      }

      try {
        const updatedAccess = transferOwnership(access, newOwnerId, userId);
        await db.updatePaletteAccess(paletteId, {
          ownerId: updatedAccess.ownerId,
          sharedWith: updatedAccess.sharedWith,
        });
        await refresh();
      } catch (err) {
        throw new Error(`Failed to transfer ownership: ${err}`);
      }
    },
    [paletteId, access, userId, refresh]
  );

  return {
    access,
    userRole,
    loading,
    error,
    canView: hasPermission('palette:view'),
    canEdit: hasPermission('palette:edit'),
    canDelete: hasPermission('palette:delete'),
    canShare: hasPermission('palette:share'),
    canExport: hasPermission('palette:export'),
    canManagePermissions: hasPermission('palette:manage_permissions'),
    hasPermission,
    shareWith,
    revokeAccess,
    makePublic,
    transferOwner,
    refresh,
  };
}
